/**
 * Validates form data against field configs (required fields, visibility).
 * Used before Save/Submit in FormRenderer and ManualAssessmentForm.
 */

import type { FormFieldConfig, FormData } from "./types";

function isFieldVisible(config: FormFieldConfig, formData: FormData): boolean {
  if (config.hide) return false;
  const showRules = config.dependencies?.show;
  if (!showRules?.length) return true;
  for (const rule of showRules) {
    const watchValue = formData[rule.when];
    if (rule.eq !== undefined && watchValue !== rule.eq) return false;
    if (rule.in !== undefined && !rule.in.includes(watchValue)) return false;
  }
  return true;
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

const DEFAULT_REQUIRED_MESSAGE = "This field is required";

/**
 * Validates form data for required fields that are visible.
 * Returns a map of field name -> error message for fields that are required but empty.
 */
export function validateFormData(
  fields: FormFieldConfig[],
  formData: FormData,
  requiredMessage: string = DEFAULT_REQUIRED_MESSAGE
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const config of fields) {
    if (!config.required) continue;
    if (!isFieldVisible(config, formData)) continue;
    const value = formData[config.field];
    if (isEmptyValue(value)) {
      errors[config.field] = requiredMessage;
    }
  }
  return errors;
}
